import * as Sentry from '@sentry/nextjs'
import get from 'lodash-es/get'
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { toaster } from '@/components/Toaster'

import { useUpdateShareReference } from '@/lib/features/SmartShares/api'

import messages from './EditableReference.messages'
import { UseEditableReferenceParams } from './EditableReference.types'
import {
  buildReferencePatchPayload,
  extractApiErrorMessage,
  hasReferenceChanged,
  normalizeReference,
  validateReference,
} from './EditableReference.utils'

/**
 * Reads the validation message off a failed request so the user sees why the save was rejected
 * rather than a generic retry prompt. Returns null when the body is missing or not JSON, in which
 * case the caller falls back to the generic message.
 */
const readApiErrorMessage = async (err: unknown) => {
  const response = get(err, 'response') as Response | undefined

  if (!response) return null

  try {
    return extractApiErrorMessage(await response.clone().json())
  } catch (parseError) {
    console.warn('Failed to parse share reference error response:', parseError)
    return null
  }
}

export const useEditableReference = ({ share, onUpdated }: UseEditableReferenceParams) => {
  const { id: shareId, reference } = share

  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(() => normalizeReference(reference))
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const { trigger: updateShareReference } = useUpdateShareReference()

  // Keep the editor in sync when the row is revalidated while not being edited.
  useEffect(() => {
    if (!isEditing) {
      setValue(normalizeReference(reference))
    }
  }, [reference, isEditing])

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const startEditing = useCallback(() => {
    setValue(normalizeReference(reference))
    setError(null)
    setIsEditing(true)
  }, [reference])

  const cancelEditing = useCallback(() => {
    setValue(normalizeReference(reference))
    setError(null)
    setIsEditing(false)
  }, [reference])

  const changeValue = useCallback((next: string) => {
    setValue(next)
    setError(validateReference(next))
  }, [])

  const save = useCallback(async () => {
    const nextReference = normalizeReference(value)
    const validationError = validateReference(nextReference)

    if (validationError) {
      setError(validationError)
      return
    }

    if (!hasReferenceChanged(reference, nextReference)) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)

    const data = buildReferencePatchPayload(share, nextReference)

    try {
      await updateShareReference({ params: { id: shareId }, data })

      setIsEditing(false)
      setError(null)
      toaster({ type: 'success', message: messages.updateSuccess.defaultMessage })
      onUpdated?.()
    } catch (err) {
      const apiMessage = await readApiErrorMessage(err)

      Sentry.captureException(err, {
        extra: { shareId, data, apiMessage },
        tags: { api: 'smart-shares', feature: 'edit-share-reference' },
      })
      setError(apiMessage ?? messages.updateError.defaultMessage)
      toaster({ type: 'error', message: messages.updateError.defaultMessage })
    } finally {
      setIsSaving(false)
    }
  }, [value, reference, share, shareId, updateShareReference, onUpdated])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        save()
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        cancelEditing()
      }
    },
    [save, cancelEditing],
  )

  return {
    cancelEditing,
    changeValue,
    error,
    handleKeyDown,
    inputRef,
    isEditing,
    isSaving,
    save,
    startEditing,
    value,
  }
}
